import React, {
  createContext,
  type Dispatch,
  type SetStateAction,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { getTreeStartingPid } from '@fs/zion-tree-groups'
import { type PID, type GroupType, type TreeId } from '@fs/zion-tree-types'

// TODO cleanup JSDocs

type StartingPersonValue = {
  loading: boolean
  startingPersonId?: PID
  setStartingPersonId: Dispatch<SetStateAction<PID | undefined>>
  refetchStartingPersonPid: () => void
}

const StartingPersonContext = createContext<StartingPersonValue | undefined>(undefined)

/**
 *
 * @returns {{
 * loading: boolean
 * startingPersonId?: string
 * setStartingPersonId: import('react').Dispatch<import('react').SetStateAction<string>>
 * refetchStartingPersonPid: () => void
 * }}
 */

export const useStartingPerson = (): StartingPersonValue => {
  const context = useContext(StartingPersonContext)
  if (!context) {
    throw new Error('useStartingPerson must be used within a StartingPersonProvider')
  }
  return context
}

/**
 *
 * @param {{
 * treeId?: string
 * types?: import('@fs/zion-tree-types').GroupType[]
 * children: import('react').ReactNode
 * }} props
 * @returns {JSX.Element}
 */

type StartingPersonProviderProps = {
  treeId?: TreeId
  types?: GroupType[]
  children: ReactNode
}

export default function StartingPersonProvider({ treeId, types, children }: StartingPersonProviderProps): JSX.Element {
  const [startingPersonId, setStartingPersonId] = useState<PID>()
  const [loading, setLoading] = useState(true)
  const isTypeTree = types?.includes('PRIVATE_PERSON_SHARING') || types?.includes('RESEARCHER_TREE')
  const lastTreeStartingPersonDataWasFetched = useRef<TreeId | false>(false)
  const isCurrent = useRef<boolean>()

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  })

  const refetchStartingPersonPid = useCallback(() => {
    lastTreeStartingPersonDataWasFetched.current = false
  }, [])

  if (treeId && lastTreeStartingPersonDataWasFetched.current !== treeId) {
    lastTreeStartingPersonDataWasFetched.current = treeId
    const noCache = true

    getTreeStartingPid(treeId, noCache)
      .then(({ personId }) => {
        if (isCurrent.current && personId && isTypeTree) {
          // TODO update getTreeStartingPid ReturnType to narrow to PID and TreeId.
          setStartingPersonId(personId as PID)
        }

        return undefined
      })
      .catch(console.error)
      .finally(() => isCurrent.current && setLoading(false))
  }

  const value = useMemo(
    () => ({ loading, startingPersonId, setStartingPersonId, refetchStartingPersonPid }),
    [refetchStartingPersonPid, startingPersonId, loading]
  )
  return <StartingPersonContext.Provider value={value}>{children}</StartingPersonContext.Provider>
}
